import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { BarMarkOptions, Encoding, MarkTransform, MarkType } from '@mode-vegas/vegas';
import { IconComponent, trackByFactory } from '@mode/capra';
import { supportsShowStackSum } from '@mode/charts/util-muze';
import { RenderDataModel } from '@mode/charts/util-vegas';
import { TransformItem, getMarks } from './layer-options-menu-helpers';

@Component({
  standalone: true,
  imports: [CommonModule, IconComponent, MatDividerModule, MatLegacyMenuModule],
  selector: 'mode-bar-options-menu',
  templateUrl: './bar-options-menu.component.html',
  styleUrls: ['./bar-options-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarOptionsMenuComponent implements OnChanges {
  @Input() model: RenderDataModel | undefined;
  @Input() encoding: Encoding | undefined;
  @Input() seriesField: string | undefined;
  @Input() markLayerOptions: BarMarkOptions | undefined;

  @Output() layerOptionsChanged = new EventEmitter<BarMarkOptions>();

  trackByValue = trackByFactory<TransformItem>(({ transform }) => transform);

  transformItems: TransformItem[] = [];
  transform: MarkTransform | undefined;

  isShowStackSum = false;
  isShowStackSumAllowed = false;

  constructor() {
    this.transformItems = [
      { label: 'Grouped', transform: MarkTransform.Group },
      { label: 'Overlapped', transform: MarkTransform.Identity },
      { label: 'Stacked', transform: MarkTransform.Stack },
      { label: '100% stacked', transform: MarkTransform.Stack100Percent },
    ];
  }

  ngOnChanges(): void {
    if (this.markLayerOptions) {
      this.transform = this.markLayerOptions.transform ?? MarkTransform.Stack;
      this.isShowStackSum = this.markLayerOptions.showStackSum ?? false;
    } else {
      this.transform = undefined;
      this.isShowStackSum = false;
    }
    this.isShowStackSumAllowed = false;
    const allMarks = getMarks(this.encoding, this.seriesField);
    if (this.model && this.encoding && this.markLayerOptions && allMarks.length > 0) {
      const marks = allMarks[0];
      this.isShowStackSumAllowed = supportsShowStackSum(this.model, this.encoding, marks, this.markLayerOptions);
    }
  }

  setTransform(transform: MarkTransform): void {
    if (this.transform !== transform) {
      this.layerOptionsChanged.emit({ type: MarkType.Bar, transform });
    }
  }

  setShowStackSum(enabled: boolean) {
    if (this.isShowStackSum !== enabled) {
      this.layerOptionsChanged.emit({ type: MarkType.Bar, showStackSum: enabled });
    }
  }
}
